import { useCitationSourceLabels } from "#features/conversations/components/conversation-source/citationSource.tsx";
import { SourcePreviewFooterLinkContext } from "#features/conversations/components/conversation-source/source-preview/SourcePreviewFooterLinkContext.ts";
import { channels, getConversationSourceChannel } from "#features/conversations/types/channels.tsx";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import type { ConversationSource } from "#types/types.ts";
import { IconOpenLink } from "@intentui/icons";
import { useContext } from "react";
import "./SourcePreview.css";

interface Props {
    source: ConversationSource;
    /** Replaces the app's name (a web page names its site). */
    appName?: string | null;
}

// Officevibe and Performance sources link to Workleap Next (app.workleap.com, app.workleap-stg.com) for organizations
// on it, so the card names Workleap there, not the classic app the source's type comes from.
const WORKLEAP_NEXT_HOST = /^app\.workleap(-[a-z]+)?\.com$/;
const NEXT_CHANNELS = [channels.WorkleapOfficevibe, channels.WorkleapPerformance];

const opensInWorkleap = (source: ConversationSource) => {
    if (!source.url || !NEXT_CHANNELS.includes(getConversationSourceChannel(source))) {
        return false;
    }

    try {
        return WORKLEAP_NEXT_HOST.test(new URL(source.url).hostname);
    } catch {
        return false;
    }
};

/** The way out, "Open in {App}": a link to the source in the hover card. A source with no URL has no footer. */
export const SourcePreviewFooter = ({ source, appName }: Props) => {
    const { tShared } = useSharedTranslation();
    const labels = useCitationSourceLabels(source);
    const link = useContext(SourcePreviewFooterLinkContext);
    const app = appName || (opensInWorkleap(source) ? tShared("channels.workleap") : labels.appName);

    if (!source.url) {
        return null;
    }

    const content = (
        <>
            {app ? tShared("openInApp", { app }) : tShared("openLink")}
            <IconOpenLink aria-hidden="true" />
        </>
    );

    if (!link) {
        return <span className="wai-source-card-footer">{content}</span>;
    }

    return (
        <a className="wai-source-card-footer" href={source.url} target="_blank" rel="noopener noreferrer" onClick={link.onClick}>
            {content}
        </a>
    );
};
